import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface ModelOptions {
  label: string;
}

export interface ProductOption {
  label: string;
  value: number;
  models: ModelOptions[];
}

interface StockPrice {
  Stock: 'InStock' | string;
  StockLevel: any | null;
  stockLevelLabel?: 'In Stock' | 'Out of Stock' | string | null;
  stockLevelLow?: boolean | null;
  Price: number;
  IsDefault: boolean;
  LastUpdatedAt: any;
  lowestPrice?: boolean | null;
  outOfStock?: boolean | null;
  belowMAP?: boolean | null;
}

interface RetailerProduct {
  AdhocDataAttributes: [
    {
      Name: 'Category' | 'Delivery Information' | 'ParentASIN' | string;
      Value: string;
    },
  ];
  Availability: 'InStock' | string;
  ClickThruUrl: string;
  FriendlyName: 'AmazonUS' | string;
  IsLocalized: boolean;
  LocationId: number;
  MatchId: string;
  OtherURLs: [
    {
      Locale: 'en-US' | string;
      Url: string;
    },
  ];
  PerformRealtimeLookup: boolean;
  Price: number;
  RetailerActive: boolean;
  RetailerAddress: any | null;
  RetailerCountry: 'US' | string;
  RetailerCurrency: 'USD' | string;
  RetailerCurrencySeparator: '.' | string;
  RetailerCurrencySymbol: '$' | string;
  RetailerCurrencyThousandsSeparator: ',' | string;
  RetailerGroupId: any | null;
  RetailerId: string;
  RetailerLogoUrl: string;
  RetailerMetaData: any[];
  RetailerProductDescription: any | null;
  RetailerProductName: string;
  RetailerName: 'AmazonUS' | string;
  RetailerSite: string;
  StockPrices: StockPrice[];
  SupportedLanguages: [
    {
      LanguageName: 'English' | string;
      CountryCode: 'en' | string;
      CultureCode: 'en-US' | string;
    },
  ];
}

export interface ChannelSightFeed {
  RetailerProducts: RetailerProduct[];
  Id: string;
  ProductId: string;
  Brand: 'MRCOOL' | string;
  ManufacturerId: string;
  ManufacturerName: 'MRCOOL' | string;
  ManufacturerImages: any[];
  ProductName: string;
  ProductDescription: string;
  ProductUrl: string;
  MainImageUrl: string;
  MainImageAltText: string;
  LocalizedDescriptions: [
    {
      CountryCode: 'US' | string;
      CountryName: 'United States' | string;
      CultureCode: 'en-US' | string;
      Description: string;
      Name: string;
      Language: any | null;
      MinimumAdvertisedPrice: number;
      ManufacturerRecommendedPrice: number;
      ProductUrl: string;
      Images: [
        {
          Url: string;
          Description: string;
        },
      ];
    },
  ];
  ProductIdentifiers: string[];
  Categories: [
    {
      HierarchyName: 'mrcool' | string;
      Category: {
        CategoryLevel: 'Category' | string;
        CategoryName: string;
        Subcategory: {
          CategoryLevel: 'Subcategory' | string;
          CategoryName: string;
          Subcategory: any | null;
        };
      };
    },
  ];
  MetaData: any | null;
  DiagnosticInfo: any[];
}

interface StoreValues {
  _hasHydrated: boolean;
  productOption: ProductOption | null;
  modelOption: ModelOptions | null;
  retailerProducts: ChannelSightFeed | null;
}

interface InputProductOptions {
  label: string;
  value: number;
  models: { label: string }[];
}

interface StoreActions {
  getProductOptions: () => InputProductOptions[];
  getFeedData: () => void;
  setSelectedProduct: (product: ProductOption | null) => void;
  setSelectedModel: (model: ModelOptions | null) => void;
  setRetailerProducts: (retailerProduct: ChannelSightFeed) => void;
  clearRetailerProducts: () => void;
}

interface ModelDetails {
  series: string;
  zones: number | null;
  model: string;
}

export type StoreState = StoreValues & StoreActions;

const initState: StoreValues = {
  _hasHydrated: false,
  productOption: null,
  modelOption: null,
  retailerProducts: null,
};

const retailerMapping: Record<string, string> = {
  ACEHardwareUS: 'ACE Hardware',
  AmazonUS: 'Amazon.com',
  ChillVenturesUS: 'Chill Ventures',
  Costco: 'Costco',
  HomeDepot: 'Home Depot',
  Lowes: "Lowe's",
  Menards: 'Menards',
  MRCOOLDirectUS: 'MRCOOL Direct',
  PremiumhomesourceUS: 'Premium Home Source',
};

const stockMap: Record<string, string> = {
  InStock: 'In Stock',
  OutOfStock: 'Out of Stock',
};

export const productGroups: Record<string, string> = {
  diy_c: '4th Gen DIY',
  diy_c_mz: '4th Gen DIY Multi-Zone',
  diy_5_bndl: '5th Gen DIY',
  diy_5_mz: '5th Gen DIY Multi-Zone',
};

export const products: Record<string, ModelDetails[]> = {
  diy_c: [
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-36-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-24-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-18-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-12-HP-C-115C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-18-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-24-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-36-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-12-HP-WM-115C25',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 2,
      model: 'DIY-MULTI2-18HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 3,
      model: 'DIY-MULTI3-27HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 4,
      model: 'DIY-MULTI4-36HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 5,
      model: 'DIY-MULTI5-48HP230C',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-12-HP-WMAH-115C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-09-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-12-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-18-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-24-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-36-HP-WMAH-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE18HP-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE09HP-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE12HP-230C25',
    },
  ],
  diy_c_mz: [
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-36-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-24-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-18-HP-C-230C25',
    },
    {
      series: 'DIY 4th Gen Condenser',
      zones: 1,
      model: 'DIY-12-HP-C-115C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-18-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-24-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-36-HP-WM-230C25',
    },
    {
      series: 'DIY 4th Gen E STAR',
      zones: 1,
      model: 'DIY-12-HP-WM-115C25',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 2,
      model: 'DIY-MULTI2-18HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 3,
      model: 'DIY-MULTI3-27HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 4,
      model: 'DIY-MULTI4-36HP230C',
    },
    {
      series: 'DIY 4th Gen Multi-Zone',
      zones: 5,
      model: 'DIY-MULTI5-48HP230C',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-12-HP-WMAH-115C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-09-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-12-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-18-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-24-HP-WMAH-230C25',
    },
    {
      series: 'DIY 4th Gen Wall Mount Air Handler',
      zones: null,
      model: 'DIY-36-HP-WMAH-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE18HP-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE09HP-230C25',
    },
    {
      series: 'DIY OuttaSight',
      zones: null,
      model: 'DIYCASSETTE12HP-230C25',
    },
  ],
  diy_5_bndl: [
    {
      series: 'DIY 5th gen Single-Zone Mini-split Bundle',
      zones: 1,
      model: 'DIY-09-HP-WM-115D25-O',
    },
    {
      series: 'DIY 5th gen Single-Zone Mini-split Bundle',
      zones: 1,
      model: 'DIY-12-HP-WM-115D25-O',
    },
    {
      series: 'DIY 5th gen Single-Zone Mini-split Bundle',
      zones: 1,
      model: 'DIY-18-HP-WM-230D25-O',
    },
    {
      series: 'DIY 5th gen Single-Zone Mini-split Bundle',
      zones: 1,
      model: 'DIY-24-HP-WM-230D25-O',
    },
    {
      series: 'DIY 5th gen Single-Zone Mini-split Bundle',
      zones: 1,
      model: 'DIY-36-HP-WM-230D25-O',
    },
  ],
  diy_5_mz: [
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: 6,
      model: 'DIY-MULTI6-55HP230D-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: null,
      model: 'DIY-12-HP-WMAH-230D25-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: 6,
      model: 'DIY-MULTI6-48HP230D-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: 5,
      model: 'DIY-MULTI5-36HP230D-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: 4,
      model: 'DIY-MULTI4-27HP230D-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: 3,
      model: 'DIY-MULTI3-18HP230D-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: null,
      model: 'DIY-09-HP-WMAH-230D25-O',
    },
    {
      series: 'DIY 5th gen Multi-Zone Mini-splits',
      zones: null,
      model: 'DIY-06-HP-WMAH-230D25-O',
    },
  ],
};

export const retailerStore = create<StoreState>()(
  persist(
    (set, get) => ({
      ...initState,
      getProductOptions: () => {
        return Object.entries(productGroups).map(([key, value], idx) => ({
          label: value,
          value: idx,
          models: products[key].map((product) => ({
            label: product.model,
          })),
        }));
      },
      getFeedData: async () => {
        const key = process.env.CHANNELSIGHT_KEY as string;
        const assetId = process.env.CHANNELSIGHT_ASSET_ID as string;
        const endpoint = process.env.CHANNELSIGHT_ENDPOINT as string;
        const pid = get().modelOption?.label.trim();
        const url = `${endpoint}?key=${key}&assetId=${assetId}&pid=${pid}`;

        try {
          const response = await fetch(url);
          const data = await response.json();

          data.length && get().setRetailerProducts(data[0]);
        } catch (err) {
          console.error(err);
        }
      },
      setSelectedProduct: (productOption: ProductOption | null) => {
        set({ productOption });
      },
      setSelectedModel: (modelOption: ModelOptions | null) => {
        set({ modelOption });
      },
      setRetailerProducts: (retailerProducts: ChannelSightFeed) => {
        const pricing: number[] = retailerProducts.RetailerProducts.flatMap(
          (product) => product.StockPrices.map((price) => price.Price)
        );
        const lowestPrice = Math.min(...pricing);
        const MAP =
          retailerProducts.LocalizedDescriptions[0].MinimumAdvertisedPrice;

        const products: RetailerProduct[] =
          retailerProducts.RetailerProducts.map((product) => ({
            ...product,
            StockPrices: product.StockPrices.map((stock) => {
              return {
                ...stock,
                lowestPrice: stock.Price === lowestPrice,
                stockLevelLabel: stockMap[stock.Stock] || null,
                stockLevelLow: stock.StockLevel && stock.StockLevel < 5,
                outOfStock: stock.StockLevel === 'OutOfStock',
                belowMAP: stock.Price < MAP,
              } as StockPrice;
            }),
          }));
        const feed: ChannelSightFeed = {
          ...retailerProducts,
          RetailerProducts: products,
        };

        console.log('feed', feed);

        set({ retailerProducts: feed });
      },
      clearRetailerProducts: () => {
        set({ retailerProducts: null });
      },
    }),
    {
      name: 'retailer-store',
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        console;
        if (!state) return;
        state._hasHydrated = true;
      },
    }
  )
);
